import { FormEvent, useEffect, useMemo, useState } from "react";

import { api } from "../../api/client";
import { Field } from '../../components/forms/Field';
import { FormModal } from '../../components/modals/FormModal';
import { ResourcesSubnav } from '../../components/subnav/ResourcesSubnav';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { apiError } from "../../lib/apiError";
import { useModalHistoryEntry } from "../../lib/modalHistory";
import { loadWithReplica, mutateOrQueue } from "../../lib/offline/helpers";
import { tagMatchesFilter } from "../../lib/tagVerificationInput";
import type { FreeOfficialTag, FreeOfficialTagsList } from "../../types";

type StatusFilter = "all" | "free" | "assigned";
type BulkScope = "assigned" | "all";

type ConfirmAction = {
  scope: BulkScope;
  title: string;
  text: string;
};

function assignedLabel(status: string): string {
  if (status === "free") {
    return "Wolny";
  }
  return "Przypisany";
}

export function FreeTagsPage() {
  const formatTag = useFormatEarTag();
  const [data, setData] = useState<FreeOfficialTagsList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  useModalHistoryEntry(confirm != null, () => setConfirm(null));

  async function load() {
    const result = await loadWithReplica<FreeOfficialTagsList>(
      "free-official-tags",
      () => api.GET("/api/free-official-tags", { params: { query: { include_assigned: true } } })
    );
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać wolnych kolczyków."));
      return;
    }
    setError(null);
    if (result.data) {
      setData(result.data);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const tags = data?.tags ?? [];
  const freeCount = data?.free_count ?? 0;
  const assignedCount = data?.assigned_count ?? 0;
  const totalCount = freeCount + assignedCount;

  const filteredTags = useMemo(() => {
    const query = search.trim();
    return tags.filter((tag) => {
      if (statusFilter !== "all" && tag.status !== statusFilter) {
        return false;
      }
      if (query && !tagMatchesFilter(tag.tag, query)) {
        return false;
      }
      return true;
    });
  }, [search, statusFilter, tags]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) {
      setError("Wklej numery kolczyków (linie lub przecinki).");
      return;
    }
    setBusy(true);
    setInfo(null);
    const result = await mutateOrQueue(
      "POST",
      "/api/free-official-tags",
      { text: text.trim() }
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się dodać kolczyków."));
      return;
    }
    const data = result.online
      ? (result.data as {
          added_count?: number;
          skipped_duplicates?: number;
          skipped_consumed?: number;
        } | undefined)
      : undefined;
    setText("");
    setAddOpen(false);
    const skippedConsumed =
      data?.skipped_consumed && data.skipped_consumed > 0
        ? ` Pominięto zużyte: ${data.skipped_consumed}.`
        : "";
    setInfo(
      data
        ? `Dodano ${data.added_count ?? 0}. Pominięto duplikaty: ${data.skipped_duplicates ?? 0}.${skippedConsumed}`
        : "Dodano kolczyki (offline).",
    );
    await load();
  }

  async function remove(tag: FreeOfficialTag) {
    setBusy(true);
    const result = await mutateOrQueue(
      "DELETE",
      `/api/free-official-tags/${tag.id}`,
      {}
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się usunąć kolczyka."));
      return;
    }
    setInfo(`Usunięto kolczyk: ${formatTag(tag.tag)}`);
    await load();
  }

  function askDeleteAssigned() {
    if (!assignedCount) {
      return;
    }
    setConfirm({
      scope: "assigned",
      title: "Usunąć zajęte numery?",
      text: `Z listy zostanie usuniętych ${assignedCount} zajętych numerów (już użytych w stadzie lub u jagniąt). Same zwierzęta nie zostaną zmienione.`,
    });
  }

  function askDeleteAll() {
    if (!totalCount) {
      return;
    }
    setConfirm({
      scope: "all",
      title: "Usunąć wszystkie numery?",
      text: `Z listy zostanie usuniętych ${totalCount} numerów. Operacja jest nieodwracalna — zwierzęta w stadzie i wykoty nie zostaną zmienione.`,
    });
  }

  async function confirmBulkDelete() {
    if (!confirm) {
      return;
    }
    setBusy(true);
    setInfo(null);
    const result = await mutateOrQueue(
      "DELETE",
      `/api/free-official-tags?scope=${confirm.scope}`,
      {}
    );
    setBusy(false);
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się usunąć kolczyków."));
      return;
    }
    const data = result.online ? (result.data as { deleted_count?: number } | undefined) : undefined;
    const count = data?.deleted_count ?? 0;
    setConfirm(null);
    setInfo(
      confirm.scope === "assigned"
        ? `Usunięto zajęte numery: ${count}.`
        : `Usunięto wszystkie numery: ${count}.`,
    );
    await load();
  }

  return (
    <main className="page" data-testid="free-tags-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Zasoby</p>
          <h1>Wolne kolczyki urzędowe</h1>
          <p className="muted">
            Wolne: {freeCount} · Przypisane: {assignedCount}
          </p>
        </div>
        <div className="actions">
          <button
            type="button"
            className="btn"
            data-testid="free-tags-add"
            onClick={() => {
              setError(null);
              setAddOpen(true);
            }}
          >
            Dodaj do puli
          </button>
        </div>
      </header>
      <ResourcesSubnav area="tags" />
      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="ok" message={info} onDismiss={() => setInfo(null)} />
      <FormModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setText("");
        }}
        title="Dodaj kolczyki do puli"
        testId="free-tags-add-form"
        onSubmit={onSubmit}
      >
        <Field label="Numery (jeden w linii albo po przecinku)">
          <textarea
            className="input"
            rows={4}
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="PL1000…"
            data-testid="free-tags-text"
          />
        </Field>
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              setAddOpen(false);
              setText("");
            }}
          >
            Anuluj
          </button>
          <button className="btn" type="submit" disabled={busy} data-testid="free-tags-save">
            Dodaj
          </button>
        </div>
      </FormModal>
      <section className="panel">
        <div className="panel__header">
          <h2 className="panel__title">Lista</h2>
          <div className="panel__header-actions actions">
            <button
              type="button"
              className="btn btn--danger btn--sm"
              data-testid="free-tags-delete-assigned"
              disabled={busy || assignedCount === 0}
              onClick={askDeleteAssigned}
            >
              Usuń zajęte
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              data-testid="free-tags-delete-all"
              disabled={busy || totalCount === 0}
              onClick={askDeleteAll}
            >
              Usuń wszystkie
            </button>
          </div>
        </div>
        <div className="filters">
          <input
            className="input"
            type="search"
            placeholder="Szukaj numeru…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            data-testid="free-tags-search"
            aria-label="Szukaj numeru kolczyka"
          />
          <div className="actions" role="group" aria-label="Filtr statusu kolczyków">
            {(
              [
                ["all", "Wszystkie"],
                ["free", "Wolne"],
                ["assigned", "Zajęte"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={statusFilter === value ? "btn btn--sm" : "btn btn--ghost btn--sm"}
                data-testid={`free-tags-filter-${value}`}
                onClick={() => setStatusFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="table-wrap">
          <ResponsiveTable className="tbl">
            <thead>
              <tr>
                <th>Kolczyk</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredTags.length === 0 ? (
                <tr className="tbl__empty-row">
                  <td colSpan={3} className="muted">
                    {tags.length === 0 ? "Pula jest pusta." : "Brak numerów spełniających filtr."}
                  </td>
                </tr>
              ) : (
                filteredTags.map((tag) => (
                  <tr key={tag.id}>
                    <td>
                      <strong>
                        <EarTag value={tag.tag} />
                      </strong>
                    </td>
                    <td>
                      {assignedLabel(tag.status)}
                    </td>
                    <td>
                      {tag.status === "free" ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={busy}
                          onClick={() => void remove(tag)}
                        >
                          Usuń
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      </section>

      {confirm ? (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          data-testid="free-tags-confirm-modal"
        >
          <div className="modal">
            <h3 className="modal__title">{confirm.title}</h3>
            <p className="modal__text">{confirm.text}</p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirm(null)}
                disabled={busy}
              >
                Anuluj
              </button>
              <button
                type="button"
                className="btn btn--danger"
                data-testid="free-tags-confirm-ok"
                disabled={busy}
                onClick={() => void confirmBulkDelete()}
              >
                Usuń
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
